import React, { useEffect, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, GripVertical, Save, Eye, EyeOff, ChevronsUp, ChevronsDown, Sparkles, Pin, Bot } from "lucide-react";

const PRESETS = [
  { name: "Green (Bull)", hex: "#22c55e" },
  { name: "Red (Bear)",   hex: "#ef4444" },
  { name: "Gold",         hex: "#f59e0b" },
  { name: "Cyan",         hex: "#22d3ee" },
  { name: "Violet",       hex: "#a78bfa" },
  { name: "Pink",         hex: "#f472b6" },
  { name: "Slate",        hex: "#e5e7eb" },
];

/**
 * Admin editor for the NYSE-style ticker tape.
 * Add / edit / delete items, pick color, reorder, activate/deactivate.
 */
export default function AdminTicker() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [meta, setMeta] = useState({ target_slots: 15, admin_count: 0, ai_count: 0 });
  const [draftId, setDraftId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/ticker/items");
      setItems(Array.isArray(data?.items) ? data.items : []);
      setMeta({
        target_slots: data?.target_slots || 15,
        admin_count: data?.admin_count || 0,
        ai_count: data?.ai_count || 0,
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load ticker items");
    }
  };

  useEffect(() => { load(); }, []);

  const broadcastRefresh = () => {
    try { window.dispatchEvent(new CustomEvent("cq:ticker:refresh")); } catch { /* no-op */ }
  };

  const addItem = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ticker/items", {
        label: "NEW ITEM",
        value: "Announcement here",
        change: "",
        color: "#22c55e",
        is_active: true,
      });
      setItems((prev) => [...prev, data]);
      setDraftId(data.id);
      broadcastRefresh();
      toast.success("Ticker item added — edit the fields on the right.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add");
    } finally { setBusy(false); }
  };

  const saveItem = async (item) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/ticker/items/${item.id}`, {
        label:     item.label,
        value:     item.value,
        change:    item.change,
        color:     item.color,
        is_active: item.is_active,
      });
      setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, ...data } : x)));
      setDraftId(null);
      broadcastRefresh();
      toast.success("Saved — ticker updated live");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this ticker item?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/ticker/items/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      broadcastRefresh();
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); }
  };

  const pinItem = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/ticker/items/${id}/pin`);
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...data } : x)));
      toast.success("Pinned — this item is now permanent");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Pin failed");
    } finally { setBusy(false); }
  };

  const regenerateAi = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post("/admin/ticker/regenerate");
      toast.success(`AI wrote ${data.created || 0} fresh items`);
      await load();
      broadcastRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Regeneration failed");
    } finally { setRegenerating(false); }
  };

  const move = async (idx, dir) => {
    const next = [...items];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    try {
      await api.post("/admin/ticker/reorder", { ids: next.map((x) => x.id) });
      broadcastRefresh();
    } catch (e) {
      toast.error("Reorder failed — refreshing…");
      load();
    }
  };

  const patch = (id, partial) => {
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...partial } : x)));
    setDraftId(id);
  };

  return (
    <AppShell>
      <div className="cq-in max-w-6xl">
        <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Admin</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-1">
          Ticker Tape
        </h1>
        <p className="text-slate-600 mt-1">
          Broadcast quick announcements to everyone in a NYSE-style scrolling ribbon at the top of the app.
          Your pinned items always take precedence — AI writes fresh content every night at midnight (ET) to fill the rest of the {meta.target_slots} slots.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button
            onClick={addItem}
            disabled={busy}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="ticker-add"
          >
            <Plus size={16} className="mr-1" /> Add ticker item
          </Button>
          <Button
            onClick={regenerateAi}
            disabled={regenerating}
            variant="outline"
            className="border-violet-200 text-violet-700 hover:bg-violet-50"
            data-testid="ticker-regenerate"
          >
            <Sparkles size={15} className="mr-1" />
            {regenerating ? "Writing…" : "Regenerate AI items"}
          </Button>
          <span className="text-xs text-slate-500 inline-flex items-center gap-3">
            <span><b className="text-slate-800">{meta.admin_count}</b> pinned</span>
            <span className="inline-flex items-center gap-1 text-violet-700"><Bot size={12} /><b>{meta.ai_count}</b> AI-generated</span>
            <span>· Target {meta.target_slots} slots</span>
          </span>
        </div>

        {/* Live preview */}
        <PreviewStrip items={items.filter((x) => x.is_active)} />

        <Card className="mt-6 rounded-2xl border-slate-100 divide-y" data-testid="ticker-list">
          {items.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-400">
              No items yet — hit “Add ticker item” to start broadcasting.
            </div>
          )}
          {items.map((it, idx) => (
            <div key={it.id} className="p-4 sm:p-5 flex gap-4 flex-col md:flex-row md:items-start" data-testid={`ticker-row-${it.id}`}>
              <div className="flex md:flex-col items-center gap-1 pt-1">
                <button
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30"
                  title="Move up"
                  data-testid={`ticker-up-${it.id}`}
                ><ChevronsUp size={16} /></button>
                <GripVertical size={14} className="text-slate-300" />
                <button
                  onClick={() => move(idx, 1)}
                  disabled={idx === items.length - 1}
                  className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30"
                  title="Move down"
                  data-testid={`ticker-down-${it.id}`}
                ><ChevronsDown size={16} /></button>
              </div>

              <div className="flex-1 min-w-0 space-y-3">
                <div className="flex items-center gap-2">
                  {it.source === "ai" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-700 border border-violet-200 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest">
                      <Bot size={11} /> AI · rewrites nightly at midnight ET
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest">
                      <Pin size={11} /> Pinned · never overwritten
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                <label className="sm:col-span-2 text-xs font-semibold text-slate-500">
                  Label
                  <Input
                    value={it.label || ""}
                    onChange={(e) => patch(it.id, { label: e.target.value })}
                    className="mt-1"
                    data-testid={`ticker-label-${it.id}`}
                  />
                </label>
                <label className="sm:col-span-3 text-xs font-semibold text-slate-500">
                  Message (value)
                  <Input
                    value={it.value || ""}
                    onChange={(e) => patch(it.id, { value: e.target.value })}
                    className="mt-1"
                    data-testid={`ticker-value-${it.id}`}
                  />
                </label>
                <label className="sm:col-span-1 text-xs font-semibold text-slate-500">
                  Change tag
                  <Input
                    value={it.change || ""}
                    onChange={(e) => patch(it.id, { change: e.target.value })}
                    placeholder="+12%"
                    className="mt-1"
                    data-testid={`ticker-change-${it.id}`}
                  />
                </label>

                <div className="sm:col-span-6 flex flex-wrap items-end gap-3">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 mb-1">Color</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={it.color || "#22c55e"}
                        onChange={(e) => patch(it.id, { color: e.target.value })}
                        className="h-9 w-12 rounded border border-slate-200 cursor-pointer"
                        data-testid={`ticker-color-${it.id}`}
                        aria-label="Pick color"
                      />
                      <div className="flex flex-wrap gap-1">
                        {PRESETS.map((p) => (
                          <button
                            key={p.hex}
                            type="button"
                            onClick={() => patch(it.id, { color: p.hex })}
                            className="h-6 w-6 rounded-full border border-slate-200 hover:scale-110 transition"
                            style={{ background: p.hex }}
                            title={p.name}
                            aria-label={p.name}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => patch(it.id, { is_active: !it.is_active })}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold border transition ${
                      it.is_active
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-50 text-slate-500 border-slate-200"
                    }`}
                    data-testid={`ticker-active-${it.id}`}
                  >
                    {it.is_active ? <Eye size={13} /> : <EyeOff size={13} />}
                    {it.is_active ? "Live" : "Hidden"}
                  </button>

                  <div className="ml-auto flex items-center gap-2">
                    {it.source === "ai" && (
                      <Button
                        onClick={() => pinItem(it.id)}
                        variant="outline"
                        className="border-violet-200 text-violet-700 hover:bg-violet-50"
                        data-testid={`ticker-pin-${it.id}`}
                      >
                        <Pin size={14} className="mr-1" /> Pin
                      </Button>
                    )}
                    <Button
                      onClick={() => deleteItem(it.id)}
                      variant="outline"
                      className="border-rose-200 text-rose-600 hover:bg-rose-50"
                      data-testid={`ticker-delete-${it.id}`}
                    >
                      <Trash2 size={14} className="mr-1" /> Delete
                    </Button>
                    <Button
                      onClick={() => saveItem(it)}
                      disabled={busy}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                      data-testid={`ticker-save-${it.id}`}
                    >
                      <Save size={14} className="mr-1" />
                      {draftId === it.id ? "Save changes" : "Save"}
                    </Button>
                  </div>
                </div>
                </div>
              </div>
            </div>
          ))}
        </Card>
      </div>
    </AppShell>
  );
}

function PreviewStrip({ items }) {
  if (!items?.length) return null;
  const doubled = [...items, ...items];
  return (
    <div className="mt-6 rounded-xl overflow-hidden border border-slate-800 bg-[#0a0a0a] relative" data-testid="ticker-preview">
      <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
      <span className="absolute left-2 top-1/2 -translate-y-1/2 z-20 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-red-600/95 text-white text-[10px] font-black uppercase tracking-widest">
        <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> Preview
      </span>
      <div
        className="flex items-center gap-10 py-2 pl-20 pr-6 whitespace-nowrap"
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          animation: `cq-tape-preview ${Math.max(24, items.length * 6)}s linear infinite`,
        }}
      >
        {doubled.map((it, i) => (
          <span key={`${it.id}-${i}`} className="inline-flex items-baseline gap-2 text-[13px] text-slate-200">
            <span className="font-black tracking-widest text-[12px]" style={{ color: it.color || "#22c55e" }}>
              {(it.label || "").toUpperCase()}
            </span>
            {it.value && <span className="font-semibold">{it.value}</span>}
            {it.change && (
              <span
                className="font-black"
                style={{
                  color:
                    (it.change || "").trim().startsWith("+") ? "#22c55e"
                      : (it.change || "").trim().startsWith("-") ? "#ef4444"
                      : (it.color || "#e5e7eb"),
                }}
              >{it.change}</span>
            )}
            <span className="text-slate-600 ml-2">•</span>
          </span>
        ))}
      </div>
      <style>{`
        @keyframes cq-tape-preview {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}
